import React, { useState, useEffect, useRef } from 'react';
import { 
  X, 
  Heart, 
  LogOut, 
  Upload, 
  Edit2, 
  Navigation, 
  Clock, 
  User,
  MapPin,
  Calendar,
  Settings,
  Eye,
  EyeOff,
  Shield,
  Trash2,
  Star,
  ThumbsUp,
  ThumbsDown,
  Menu,
  ChevronRight
} from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { Location, UserLocation } from '../types';
import { LocationCard } from './LocationCard';
import { supabase, updateUserProfile, uploadAvatar, getUserVisitedLocations, updateVisitReview, createUserProfile, deleteUserAccount } from '../utils/supabase';
import { LocationProfile } from './LocationProfile';
import { useLocation } from '../context/LocationContext';
import { getCachedRoute } from '../utils/routes';
import { getFavoriteLocationWord, getVisitedLocationWord } from '../utils/grammar';
import toast from 'react-hot-toast';

interface UserProfileProps {
  isOpen: boolean;
  onClose: () => void;
}

interface Profile {
  name: string;
  avatar_url: string;
}

type TabType = 'overview' | 'favorites' | 'visited' | 'settings';

export const UserProfile: React.FC<UserProfileProps> = ({ isOpen, onClose }) => {
  const { user, signOut } = useAuth();
  const [activeTab, setActiveTab] = useState<TabType>('overview');
  const [favorites, setFavorites] = useState<Location[]>([]);
  const [visited, setVisited] = useState<{ location: Location; liked: boolean | null }[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDeleting, setIsDeleting] = useState(false);
  const [profile, setProfile] = useState<Profile>({ name: '', avatar_url: '' });
  const [isEditing, setIsEditing] = useState(false);
  const [editName, setEditName] = useState('');
  const [selectedLocation, setSelectedLocation] = useState<Location | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [routesInfo, setRoutesInfo] = useState<Record<string, { distance: number; duration: number }>>({});
  const { userLocation } = useLocation();
  const [isChangingPassword, setIsChangingPassword] = useState(false);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isMobileSidebarOpen, setIsMobileSidebarOpen] = useState(false);

  useEffect(() => {
    if (isOpen && user) {
      loadProfile();
      loadFavorites();
      loadVisited();

      if (user.app_metadata?.provider === 'google') {
        setProfile(prev => ({
          ...prev,
          name: user.user_metadata?.full_name || '',
          avatar_url: user.user_metadata?.avatar_url || ''
        }));
      }
    }
  }, [isOpen, user]);

  const loadProfile = async () => {
    if (!user) return;
    const {
      data: { session }
    } = await supabase.auth.getSession();
    if (!session?.user) {
      console.error('Not authenticated - cannot load profile');
      return;
    }
    try {
      const { data, error } = await supabase
        .from('profiles')
        .select('name, avatar_url')
        .eq('id', user.id)
        .maybeSingle();

      if (error) throw error;

      if (data) {
        setProfile(data);
        setEditName(data.name || '');
      } else {

        const { data: created, error: createError } = await createUserProfile({ name: '', avatar_url: '' });
        if (createError || !created) {
          toast.error(createError ?? 'Не атрымалася стварыць профіль');
          setProfile({ name: '', avatar_url: '' });
          setEditName('');
        } else {
          setProfile(created);
          setEditName(created.name || '');
        }
      }
    } catch (error) {
      console.error('Error loading profile:', error);
      toast.error('Не атрымалася загрузіць профіль');
    }
  };

  const loadFavorites = async () => {
    if (!user) return;
    try {
      setIsLoading(true);

      const { data, error } = await supabase
        .from('favorites')
        .select(`
          location_id,
          locations:location_id (
            id,
            name,
            description,
            short_description,
            category,
            coordinates,
            images,
            tags,
            status,
            created_at,
            updated_at,
            is_verified,
            search_vector,
            short_id
          )
        `)
        .eq('user_id', user.id);

      if (error) throw error;

      const locations = data
        .map(item => {
          const location = item.locations;
          if (!location) return null;
          
          return {
            ...location,
            shortDescription: location.short_description,
            images: location.images.map((image: string) => 
              image.startsWith('http') ? image : `${supabase.storage.from('location-images').getPublicUrl(image).data.publicUrl}`
            ),
            tags: location.tags || []
          };
        })
        .filter((location): location is Location => location !== null);

      setFavorites(locations);
    } catch (error) {
      console.error('Error loading favorites:', error);
      toast.error('Не атрымалася загрузіць абраныя лакацыі');
    } finally {
      setIsLoading(false);
    }
  };

  const loadVisited = async () => {
    if (!user) return;
    try {
      const data = await getUserVisitedLocations();
      setVisited(data);
    } catch (error) {
      console.error('Error loading visited locations:', error);
    }
  };

  useEffect(() => {
    if (!userLocation || favorites.length === 0) return;
    setRoutesInfo({});
    favorites.forEach((loc) => {
      if (loc.coordinates) {
        getCachedRoute(userLocation as UserLocation, loc.coordinates)
          .then(({ distance, duration }) => {
            setRoutesInfo((prev) => ({ ...prev, [loc.id]: { distance, duration } }));
          })
          .catch(console.error);
      }
    });
  }, [favorites, userLocation]);

  const handleAvatarChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    try {
      const file = e.target.files?.[0];
      if (!file || !user) return;

      const publicUrl = await uploadAvatar(file, user.id);
      const { data, error } = await updateUserProfile({ avatar_url: publicUrl });

      if (error || !data) {
        toast.error(error ?? 'Не атрымалася абнавіць фота');
        return;
      }

      setProfile(data);
      toast.success('Фота профілю абноўлена');
    } catch (error) {
      console.error('Error updating avatar:', error);
      toast.error('Не атрымалася абнавіць фота');
    }
  };

  const handleNameSave = async () => {
    try {
      if (!user) return;
      const { data, error } = await updateUserProfile({ name: editName });
      if (error || !data) {
        toast.error(error ?? 'Не атрымалася абнавіць імя');
        return;
      }
      setProfile(data);
      setIsEditing(false);
      toast.success('Імя паспяхова абноўлена');
    } catch (error) {
      console.error('Error updating name:', error);
      toast.error('Не атрымалася абнавіць імя');
    }
  };

  const handleDeleteAccount = async () => {
    const confirmed = window.confirm(
      'Вы ўпэўнены, што жадаеце выдаліць свой акаўнт? Гэта дзеянне нельга адмяніць. Усе вашы дадзеныя будуць выдалены назаўсёды.'
    );

    if (!confirmed || !user) return;

    const doubleConfirmed = window.confirm(
      'Апошняе папярэджанне! Пасля выдалення акаўнта вы страціце доступ да ўсіх сваіх дадзеных. Вы сапраўды жадаеце працягнуць?'
    );

    if (!doubleConfirmed) return;

    try {
      setIsDeleting(true);
      
      const { error } = await deleteUserAccount();
      
      if (error) {
        throw new Error(error);
      }

      // Close the profile modal
      onClose();
      
      // Show success message
      toast.success('Акаўнт паспяхова выдалены');
      
      // Redirect to home page
      window.location.href = '/';
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error(error instanceof Error ? error.message : 'Не атрымалася выдаліць акаўнт');
    } finally {
      setIsDeleting(false);
    }
  };

  const handleLocationSelect = (location: Location) => {
    setSelectedLocation(location);
  };

  const handleLocationClose = () => {
    setSelectedLocation(null);
  };

  const formatDuration = (minutes: number) => {
    if (minutes > 60) {
      const hours = minutes / 60;
      const roundedHours = Math.round(hours * 10) / 10;
      return `${Number.isInteger(roundedHours) ? Math.floor(roundedHours) : roundedHours} г.`;
    }
    return `${minutes} хв`;
  };

  const handleRemoveFromFavorites = async (locationId: string) => {
    try {
      const { error } = await supabase
        .from('favorites')
        .delete()
        .match({ user_id: user?.id, location_id: locationId });

      if (error) throw error;

      setFavorites(prev => prev.filter(loc => loc.id !== locationId));
      toast.success('Выдалена з абранага');
    } catch (error) {
      console.error('Error removing from favorites:', error);
      toast.error('Не атрымалася выдаліць з абранага');
    }
  };

  const handleVisitedReview = async (locationId: string, liked: boolean) => {
    try {
      const { error } = await updateVisitReview(locationId, liked);
      if (error) throw error;
      
      // Обновляем локальное состояние
      setVisited(prev => prev.map(v => 
        v.location.id === locationId ? { ...v, liked } : v
      ));
      
      toast.success('Водгук абноўлены');
    } catch (error) {
      console.error('Error updating review:', error);
      toast.error('Не атрымалася абнавіць водгук');
    }
  };

  const handleRemoveVisit = async (locationId: string) => {
    const confirmed = window.confirm('Вы ўпэўнены, што жадаеце адмяніць наведванне гэтай лакацыі?');
    if (!confirmed) return;

    try {
      const { error } = await supabase
        .from('visits')
        .delete()
        .match({ user_id: user?.id, location_id: locationId });

      if (error) throw error;

      setVisited(prev => prev.filter(v => v.location.id !== locationId));
      toast.success('Наведванне адменена');
    } catch (error) {
      console.error('Error removing visit:', error);
      toast.error('Не атрымалася адмяніць наведванне');
    }
  };

  const handlePasswordUpdate = async () => {
    if (newPassword !== confirmPassword) {
      toast.error('Паролі не супадаюць');
      return;
    }

    if (newPassword.length < 6) {
      toast.error('Пароль павінен змяшчаць не менш за 6 сімвалаў');
      return;
    }

    try {
      setIsUpdatingPassword(true);
      const { error } = await supabase.auth.updateUser({ password: newPassword });
      if (error) throw error;
      toast.success('Пароль абноўлены');
      setIsChangingPassword(false);
      setNewPassword('');
      setConfirmPassword('');
    } catch (error) {
      console.error('Error updating password:', error);
      toast.error('Не атрымалася абнавіць пароль');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  if (!isOpen || !user) return null;

  const displayName = profile.name || user.user_metadata?.full_name || user.email;
  const totalVisited = visited.length;
  const totalLiked = visited.filter(v => v.liked === true).length;
  const totalFavorites = favorites.length;

  const tabs = [
    { id: 'overview', label: 'Агляд', icon: User },
    { id: 'favorites', label: 'Абранае', icon: Heart, count: totalFavorites },
    { id: 'visited', label: 'Наведаныя', icon: MapPin, count: totalVisited },
    { id: 'settings', label: 'Налады', icon: Settings },
  ] as const;

  return (
    <div className="fixed inset-0 bg-black/60 backdrop-blur-sm z-50 flex items-center justify-center p-2 sm:p-4">
      <div className="bg-white rounded-2xl sm:rounded-3xl w-full max-w-6xl h-[70vh] sm:h-[65vh] overflow-hidden shadow-2xl flex flex-col">
        
        {/* Mobile Header */}
        <div className="lg:hidden flex items-center justify-between p-3 sm:p-4 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10">
          <button
            onClick={() => setIsMobileSidebarOpen(!isMobileSidebarOpen)}
            className="p-2 hover:bg-white/50 rounded-full transition-colors"
          >
            <Menu size={20} />
          </button>
          <h2 className="text-lg sm:text-xl font-bold text-gray-900">Профіль</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-full transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        {/* Desktop Header */}
        <div className="hidden lg:flex items-center justify-between p-6 border-b border-gray-200 bg-gradient-to-r from-primary/5 to-primary/10">
          <h2 className="text-2xl font-bold text-gray-900">Профіль карыстальніка</h2>
          <button
            onClick={onClose}
            className="p-2 hover:bg-white/50 rounded-full transition-colors"
          >
            <X size={24} />
          </button>
        </div>

        <div className="flex flex-1 min-h-0">
          
          {/* Mobile Sidebar Overlay */}
          {isMobileSidebarOpen && (
            <div className="lg:hidden fixed inset-0 bg-black/50 z-10" onClick={() => setIsMobileSidebarOpen(false)} />
          )}

          {/* Sidebar */}
          <div className={`
            ${isMobileSidebarOpen ? 'translate-x-0' : '-translate-x-full'} 
            lg:translate-x-0 lg:relative fixed left-0 top-0 h-full
            w-72 sm:w-80 border-r border-gray-200 bg-white z-20 transition-transform duration-300 ease-in-out
            flex flex-col
          `}>
            
            {/* Profile Info */}
            <div className="p-4 sm:p-6 border-b border-gray-200 flex-shrink-0">
              <div className="flex flex-col items-center text-center">
                <div className="relative mb-4">
                  <div className="w-16 sm:w-20 h-16 sm:h-20 rounded-full overflow-hidden bg-gray-100 ring-4 ring-white shadow-lg">
                    {profile.avatar_url ? (
                      <img
                        src={profile.avatar_url}
                        alt="Avatar"
                        className="w-full h-full object-cover"
                      />
                    ) : (
                      <div className="w-full h-full flex items-center justify-center bg-primary text-white text-xl sm:text-2xl font-bold">
                        {displayName[0].toUpperCase()}
                      </div>
                    )}
                  </div>
                  <button
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-0 right-0 p-1.5 bg-primary text-white rounded-full shadow-lg hover:bg-primary/90 transition-colors"
                  >
                    <Upload size={10} />
                  </button>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handleAvatarChange}
                  />
                </div>

                {isEditing ? (
                  <div className="w-full space-y-2">
                    <input
                      type="text"
                      value={editName}
                      onChange={(e) => setEditName(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary text-sm text-center"
                      placeholder="Увядзіце імя"
                    />
                    <div className="flex gap-2">
                      <button
                        onClick={handleNameSave}
                        className="flex-1 px-3 py-1.5 bg-primary text-white rounded-lg hover:bg-primary/90 text-xs"
                      >
                        Захаваць
                      </button>
                      <button
                        onClick={() => {
                          setIsEditing(false);
                          setEditName(profile.name);
                        }}
                        className="flex-1 px-3 py-1.5 border border-gray-300 rounded-lg hover:bg-gray-50 text-xs"
                      >
                        Адмяніць
                      </button>
                    </div>
                  </div>
                ) : (
                  <div className="w-full">
                    <div className="flex items-center justify-center gap-2 mb-1">
                      <h3 className="text-base sm:text-lg font-bold text-gray-900 truncate">
                        {displayName}
                      </h3>
                      <button
                        onClick={() => setIsEditing(true)}
                        className="p-1 hover:bg-gray-200 rounded-full"
                      >
                        <Edit2 size={12} />
                      </button>
                    </div>
                    <p className="text-xs sm:text-sm text-gray-600 truncate">{user.email}</p>
                  </div>
                )}
              </div>
            </div>

            {/* Navigation Tabs */}
            <div className="p-3 sm:p-4 flex-1 overflow-y-auto">
              <nav className="space-y-1">
                {tabs.map((tab) => {
                  const Icon = tab.icon;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => {
                        setActiveTab(tab.id);
                        setIsMobileSidebarOpen(false);
                      }}
                      className={`w-full flex items-center justify-between px-3 sm:px-4 py-2.5 sm:py-3 rounded-xl transition-all duration-200 ${
                        activeTab === tab.id
                          ? 'bg-primary text-white shadow-lg'
                          : 'text-gray-700 hover:bg-gray-100'
                      }`}
                    >
                      <div className="flex items-center gap-2 sm:gap-3">
                        <Icon size={18} />
                        <span className="font-medium text-sm sm:text-base">{tab.label}</span>
                      </div>
                      <div className="flex items-center gap-2">
                        {tab.count !== undefined && (
                          <span className={`px-2 py-1 rounded-full text-xs font-bold ${
                            activeTab === tab.id
                              ? 'bg-white/20 text-white'
                              : 'bg-gray-200 text-gray-600'
                          }`}>
                            {tab.count}
                          </span>
                        )}
                        <ChevronRight size={14} className="lg:hidden" />
                      </div>
                    </button>
                  );
                })}
              </nav>
            </div>
          </div>

          {/* Main Content */}
          <div className="flex-1 overflow-y-auto">
            
            {/* Overview Tab */}
            {activeTab === 'overview' && (
              <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
                <div>
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900 mb-3 sm:mb-4">Агляд актыўнасці</h3>
                  
                  {/* Activity Stats */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-3 sm:gap-4 mb-4 sm:mb-6">
                    <div className="p-3 sm:p-4 lg:p-6 bg-gradient-to-br from-primary/10 to-primary/5 rounded-2xl border border-primary/20">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-primary/20 rounded-full">
                          <Heart className="w-4 sm:w-6 h-4 sm:h-6 text-primary" />
                        </div>
                        <div>
                          <div className="text-xl sm:text-2xl font-bold text-gray-900">{totalFavorites}</div>
                          <div className="text-sm sm:text-base text-gray-600">
                            {getFavoriteLocationWord(totalFavorites)}
                          </div>
                        </div>
                      </div>
                    </div>
                    
                    <div className="p-3 sm:p-4 lg:p-6 bg-gradient-to-br from-green-100 to-green-50 rounded-2xl border border-green-200">
                      <div className="flex items-center gap-3 sm:gap-4">
                        <div className="p-2 sm:p-3 bg-green-200 rounded-full">
                          <MapPin className="w-4 sm:w-6 h-4 sm:h-6 text-green-700" />
                        </div>
                        <div>
                          <div className="text-xl sm:text-2xl font-bold text-gray-900">{totalVisited}</div>
                          <div className="text-sm sm:text-base text-gray-600">
                            {getVisitedLocationWord(totalVisited)}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Recent Activity */}
                  <div>
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Апошняя актыўнасць</h4>
                    {visited.length > 0 ? (
                      <div className="space-y-2 sm:space-y-3">
                        {visited.slice(0, 3).map((visit) => (
                          <div key={visit.location.id} className="flex items-center gap-3 sm:gap-4 p-3 sm:p-4 bg-gray-50 rounded-xl">
                            <div className="w-10 sm:w-12 h-10 sm:h-12 bg-primary/10 rounded-full flex items-center justify-center">
                              <MapPin className="w-4 sm:w-6 h-4 sm:h-6 text-primary" />
                            </div>
                            <div className="flex-1">
                              <h5 className="font-medium text-gray-900 text-sm sm:text-base">{visit.location.name}</h5>
                              <p className="text-xs sm:text-sm text-gray-600">Наведана</p>
                            </div>
                            {visit.liked !== null && (
                              <div className={`p-1.5 sm:p-2 rounded-full ${visit.liked ? 'bg-green-100' : 'bg-red-100'}`}>
                                {visit.liked ? (
                                  <ThumbsUp className="w-3 sm:w-4 h-3 sm:h-4 text-green-600" />
                                ) : (
                                  <ThumbsDown className="w-3 sm:w-4 h-3 sm:h-4 text-red-600" />
                                )}
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    ) : (
                      <div className="text-center py-6 sm:py-8 text-gray-500">
                        <MapPin className="w-10 sm:w-12 h-10 sm:h-12 mx-auto mb-2 sm:mb-3 text-gray-300" />
                        <p className="text-sm sm:text-base">Вы яшчэ не наведалі ніводнай лакацыі</p>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Абраныя лакацыі</h3>
                  <span className="px-2 sm:px-3 py-1 bg-primary/10 text-primary rounded-full text-xs sm:text-sm font-medium">
                    {totalFavorites} {getFavoriteLocationWord(totalFavorites)}
                  </span>
                </div>
                
                {isLoading ? (
                  <div className="text-center py-6 sm:py-8">
                    <div className="animate-spin h-6 sm:h-8 w-6 sm:w-8 border-4 border-primary border-t-transparent rounded-full mx-auto"></div>
                  </div>
                ) : favorites.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <Heart className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
                    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Няма абраных лакацый</h4>
                    <p className="text-sm sm:text-base text-gray-600">Дадайце лакацыі ў абранае, каб яны з'явіліся тут</p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-4">
                    {favorites.map((location) => (
                      <div
                        key={location.id}
                        className="group p-3 sm:p-4 bg-gray-50 rounded-xl hover:bg-gray-100 transition-colors cursor-pointer"
                        onClick={() => handleLocationSelect(location)}
                      >
                        <div className="flex gap-3 sm:gap-4">
                          <div className="w-12 sm:w-16 h-12 sm:h-16 rounded-lg overflow-hidden bg-gray-200 flex-shrink-0">
                            {location.images[0] ? (
                              <img
                                src={location.images[0]}
                                alt={location.name}
                                className="w-full h-full object-cover"
                              />
                            ) : (
                              <div className="w-full h-full flex items-center justify-center">
                                <MapPin className="w-4 sm:w-6 h-4 sm:h-6 text-gray-400" />
                              </div>
                            )}
                          </div>
                          
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 truncate group-hover:text-primary transition-colors text-sm sm:text-base">
                              {location.name}
                            </h4>
                            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2 mb-2">
                              {location.shortDescription}
                            </p>
                            
                            <div className="flex items-center gap-3 sm:gap-4 text-xs text-gray-500">
                              {routesInfo[location.id] && (
                                <>
                                  <div className="flex items-center gap-1">
                                    <Navigation size={10} />
                                    <span>{routesInfo[location.id].distance} км</span>
                                  </div>
                                  <div className="flex items-center gap-1">
                                    <Clock size={10} />
                                    <span>{formatDuration(routesInfo[location.id].duration)}</span>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>
                          
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleRemoveFromFavorites(location.id);
                            }}
                            className="p-1.5 sm:p-2 text-red-500 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-all"
                          >
                            <Heart size={14} className="fill-current" />
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Visited Tab */}
            {activeTab === 'visited' && (
              <div className="p-3 sm:p-4 lg:p-6">
                <div className="flex items-center justify-between mb-4 sm:mb-6">
                  <h3 className="text-lg sm:text-xl font-bold text-gray-900">Наведаныя лакацыі</h3>
                  <span className="px-2 sm:px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs sm:text-sm font-medium">
                    {totalVisited} {getVisitedLocationWord(totalVisited)}
                  </span>
                </div>
                
                {visited.length === 0 ? (
                  <div className="text-center py-8 sm:py-12">
                    <MapPin className="w-12 sm:w-16 h-12 sm:h-16 mx-auto mb-3 sm:mb-4 text-gray-300" />
                    <h4 className="text-base sm:text-lg font-medium text-gray-900 mb-2">Няма наведаных лакацый</h4>
                    <p className="text-sm sm:text-base text-gray-600">Адзначце лакацыі як наведаныя, каб яны з'явіліся тут</p>
                  </div>
                ) : (
                  <div className="space-y-2 sm:space-y-3">
                    {visited.map(v => (
                      <div key={v.location.id} className="relative p-3 sm:p-4 bg-gray-50 rounded-xl">
                        {/* Remove Button */}
                        <button
                          onClick={() => handleRemoveVisit(v.location.id)}
                          className="absolute top-2 right-2 p-1.5 text-gray-400 hover:text-red-500 hover:bg-red-50 rounded-full transition-colors"
                          title="Адмяніць наведванне"
                        >
                          <X size={14} />
                        </button>

                        <div className="flex items-start justify-between gap-3 sm:gap-4 pr-8">
                          <div className="flex-1 min-w-0">
                            <h4 className="font-semibold text-gray-900 mb-1 text-sm sm:text-base truncate">{v.location.name}</h4>
                            <p className="text-xs sm:text-sm text-gray-600 line-clamp-2">{v.location.shortDescription}</p>
                          </div>
                          
                          <div className="flex flex-col gap-2 flex-shrink-0">
                            {v.liked === null ? (
                              <div className="flex gap-2">
                                <button 
                                  onClick={() => handleVisitedReview(v.location.id, true)} 
                                  className="p-2 bg-gray-200 hover:bg-green-500 hover:text-white rounded-lg transition-colors"
                                  title="Спадабалася"
                                >
                                  <ThumbsUp size={14} />
                                </button>
                                <button 
                                  onClick={() => handleVisitedReview(v.location.id, false)} 
                                  className="p-2 bg-gray-200 hover:bg-red-500 hover:text-white rounded-lg transition-colors"
                                  title="Не спадабалася"
                                >
                                  <ThumbsDown size={14} />
                                </button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-2">
                                <button 
                                  onClick={() => handleVisitedReview(v.location.id, true)} 
                                  className={`p-2 rounded-lg transition-colors ${
                                    v.liked 
                                      ? 'bg-green-500 text-white' 
                                      : 'bg-gray-200 hover:bg-green-500 hover:text-white'
                                  }`}
                                  title="Спадабалася"
                                >
                                  <ThumbsUp size={14} />
                                </button>
                                <button 
                                  onClick={() => handleVisitedReview(v.location.id, false)} 
                                  className={`p-2 rounded-lg transition-colors ${
                                    !v.liked 
                                      ? 'bg-red-500 text-white' 
                                      : 'bg-gray-200 hover:bg-red-500 hover:text-white'
                                  }`}
                                  title="Не спадабалася"
                                >
                                  <ThumbsDown size={14} />
                                </button>
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Settings Tab */}
            {activeTab === 'settings' && (
              <div className="p-3 sm:p-4 lg:p-6 space-y-4 sm:space-y-6">
                <h3 className="text-lg sm:text-xl font-bold text-gray-900">Налады акаўнта</h3>
                
                {/* Password Change */}
                <div className="p-3 sm:p-4 lg:p-6 bg-gray-50 rounded-2xl">
                  <div className="flex items-center gap-2 sm:gap-3 mb-3 sm:mb-4">
                    <Shield className="w-4 sm:w-5 h-4 sm:h-5 text-primary" />
                    <h4 className="text-base sm:text-lg font-semibold text-gray-900">Бяспека</h4>
                  </div>
                  
                  {isChangingPassword ? (
                    <div className="space-y-3 sm:space-y-4">
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Новы пароль</label>
                        <div className="relative">
                          <input
                            type={showPassword ? "text" : "password"}
                            value={newPassword}
                            onChange={(e) => setNewPassword(e.target.value)}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary pr-10 sm:pr-12 text-sm sm:text-base"
                            placeholder="Увядзіце новы пароль"
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword(!showPassword)}
                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      
                      <div>
                        <label className="block text-xs sm:text-sm font-medium text-gray-700 mb-2">Пацвердзіце пароль</label>
                        <div className="relative">
                          <input
                            type={showConfirmPassword ? "text" : "password"}
                            value={confirmPassword}
                            onChange={(e) => setConfirmPassword(e.target.value)}
                            className="w-full px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg focus:ring-primary focus:border-primary pr-10 sm:pr-12 text-sm sm:text-base"
                            placeholder="Пацвердзіце новы пароль"
                            minLength={6}
                          />
                          <button
                            type="button"
                            onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                            className="absolute right-2 sm:right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                          >
                            {showConfirmPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                          </button>
                        </div>
                      </div>
                      
                      <div className="flex flex-col sm:flex-row gap-2 sm:gap-3">
                        <button
                          onClick={handlePasswordUpdate}
                          disabled={isUpdatingPassword || !newPassword || !confirmPassword}
                          className="flex-1 px-3 sm:px-4 py-2 sm:py-3 bg-primary text-white rounded-lg hover:bg-primary/90 disabled:opacity-50 disabled:cursor-not-allowed font-medium text-sm sm:text-base"
                        >
                          {isUpdatingPassword ? 'Змена...' : 'Захаваць пароль'}
                        </button>
                        <button
                          onClick={() => {
                            setIsChangingPassword(false);
                            setNewPassword('');
                            setConfirmPassword('');
                          }}
                          className="px-3 sm:px-4 py-2 sm:py-3 border border-gray-300 rounded-lg hover:bg-gray-50 font-medium text-sm sm:text-base"
                        >
                          Адмяніць
                        </button>
                      </div>
                    </div>
                  ) : (
                    <button
                      onClick={() => setIsChangingPassword(true)}
                      className="px-3 sm:px-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
                    >
                      Змяніць пароль
                    </button>
                  )}
                </div>

                {/* Account Actions */}
                <div className="p-3 sm:p-4 lg:p-6 bg-gray-50 rounded-2xl">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3 sm:mb-4">Дзеянні з акаўнтам</h4>
                  
                  <div className="space-y-2 sm:space-y-3">
                    <button
                      onClick={() => signOut()}
                      className="w-full flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors font-medium text-sm sm:text-base"
                    >
                      <LogOut size={18} />
                      <span>Выйсці з акаўнта</span>
                    </button>

                    <button
                      onClick={handleDeleteAccount}
                      disabled={isDeleting}
                      className="w-full flex items-center justify-center gap-2 sm:gap-3 px-3 sm:px-4 py-2 sm:py-3 border border-red-300 text-red-600 rounded-lg hover:bg-red-50 transition-colors disabled:opacity-50 font-medium text-sm sm:text-base"
                    >
                      <Trash2 size={18} />
                      <span>
                        {isDeleting ? 'Выдаленне...' : 'Выдаліць акаўнт'}
                      </span>
                    </button>
                  </div>
                </div>

                {/* Account Info */}
                <div className="p-3 sm:p-4 lg:p-6 bg-blue-50 rounded-2xl border border-blue-200">
                  <h4 className="text-base sm:text-lg font-semibold text-gray-900 mb-3">Інфармацыя пра акаўнт</h4>
                  <div className="space-y-2 text-xs sm:text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Email:</span>
                      <span className="font-medium truncate ml-2">{user.email}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Дата рэгістрацыі:</span>
                      <span className="font-medium">
                        {new Date(user.created_at).toLocaleDateString('be-BY')}
                      </span>
                    </div>
                    {user.app_metadata?.provider && (
                      <div className="flex justify-between">
                        <span className="text-gray-600">Спосаб уваходу:</span>
                        <span className="font-medium capitalize">{user.app_metadata.provider}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>

      {selectedLocation && (
        <LocationProfile
          location={selectedLocation}
          onClose={handleLocationClose}
          userLocation={userLocation}
        />
      )}
    </div>
  );
};